import React, {cloneElement, useCallback, useId, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
    DotsThreeVertical,
    Warning,
    CheckCircle,
    ArrowsClockwise,
    ArrowRight,
    Plus,
    Lightning,
    SquaresFour,
    Brain,
    Gear,
    TrendUp,
    TrendDown,
    Minus,
    UsersThree,
    Power,
    Trash,
    BookOpen,
    PencilSimple,
} from '../../icons';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {resolveErrorKind} from '../../channels/adAccountErrorKinds';
import {getMetricName} from '../../dashboard/columns';
import {useDismissOnOutside} from '../../../hooks/useDismissOnOutside';
import {AlertsPopover} from './AlertsPopover';
import {ControlLineCta} from './ControlLineCta';
import {lineDefinition} from '../../client/panel_v11/lineDefinition';
import {LOCALE} from '../../client/panel_v11/panelFormatters';
import {ClientAvatar, clientIconUrl, nameInitials} from '../../elements/ClientAvatar';
import {Sparkline, activityTotal, activityTrend} from './Sparkline';
import {
    ACCOUNTS_DOWN,
    SEVERITY_GOLD,
    SEVERITY_RED,
    SPEND_REASON_NO_SPEND,
    spendState,
    spendReason,
    goalsState,
    hasGoalsPace,
    attentionReason,
    accountsSeverity,
    accountCurrencyMismatch,
    downAccountsCount,
    evaluatedGoalsTotal,
} from './clientsV2Thresholds';
import {defineGoalPath} from '../../client/panel_v11/linesViewLogic';
import {anchorAttr} from '../../../guides/anchors';
import {ClientEconomicsCells, VIEW_ECONOMICS} from './economics/ClientEconomicsRow';

const MAX_VISIBLE_ACCOUNTS = 3;
const MAX_VISIBLE_TEAM = 3;

const TREND_ICON = {
    up: TrendUp,
    down: TrendDown,
    flat: Minus,
};

function formatAmount(value) {
    return Math.round(Number(value) || 0).toLocaleString('es-AR');
}

// Los porcentajes del motivo y del tooltip: enteros y con el formato del panel (es-AR fijo, D3 de la
// tanda iVirtual), así «1.645 %» se lee igual que los montos de al lado. Sin decimales a propósito
// (D5): con el es-AR fijo, un «4,8» se lee mal en la interfaz en inglés.
function formatPct(value) {
    return (Math.round(Number(value)) || 0).toLocaleString(LOCALE);
}

// Valores de objetivos: hasta 2 decimales (un ROAS es "4,2", un costo "154.234").
function formatGoalValue(value) {
    return Number(value).toLocaleString('es-AR', {maximumFractionDigits: 2});
}

// Fecha local YYYY-MM-DD de hace N días — para armar los deep-links a la Bitácora con la misma
// ventana que cuentan el sparkline (14 días) y la pill de alertas (7 días).
function localDateDaysAgo(daysAgo) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

// Las capas que cuenta el sparkline: todo menos los cambios de plataforma. Es EL contrato con la
// Bitácora — el número del listado tiene que ser lo que se ve al aterrizar con estas capas.
const ACTIVITY_DEEP_LINK_LAYERS = 'memory,budget,configuration,comments,team,alerts';

// "11 de agosto" — hasta cuándo dura el silencio más largo. Devuelve `null` si no hay fecha (algún
// silencio es para siempre) o si el backend mandó algo que no se puede parsear: ahí la fila dice
// cuántas hay silenciadas sin prometer una fecha, en vez de mostrar "Invalid Date".
export function snoozedUntilLabel(isoDate, language) {
    if (!isoDate) return null;
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString(language || undefined, {day: 'numeric', month: 'long'});
}

function formatMoney(currency, value) {
    const amount = formatAmount(value);
    return currency ? `${currency} ${amount}` : amount;
}

/**
 * Una fila del Listado de Clientes v2. Es un grid de 7 columnas (las que no entran se descartan
 * por ancho, ver `_clients-v2.scss`); el borde izquierdo codifica la severidad calculada en
 * `clientsV2Thresholds`.
 *
 * Los clientes sin líneas de control no muestran métricas: en su lugar va una celda de setup que
 * abarca el ancho de las columnas de datos y lleva a crear la primera línea.
 */
export function ClientV2Row(props) {
    const {t, i18n} = useTranslation();
    const {client, severity, elapsedFraction, currentUserId, memoryEnabled, mediaPlanEnabled} = props;
    // El cartel con la definición del conteo de líneas: lo describe para el lector de pantalla.
    const linesDefinitionId = useId();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const closeMenu = useCallback(() => setIsMenuOpen(false), []);

    const [isAlertsOpen, setIsAlertsOpen] = useState(false);
    // Silenciar tiene que bajar el número EN EL ACTO. El listado no se recarga por una fila, así
    // que el popover devuelve los contadores nuevos y esta fila los usa por encima de los del
    // payload hasta la próxima carga de la pantalla.
    const [alertCounts, setAlertCounts] = useState(null);
    // El modal de silenciar se monta en un portal, fuera del ref: sin esto, el primer click adentro
    // se lee como "click afuera" y cierra el popover que lo abrió.
    const [isAlertsLocked, setIsAlertsLocked] = useState(false);
    const alertsRef = useRef(null);
    const closeAlerts = useCallback(() => setIsAlertsOpen(false), []);

    useDismissOnOutside(menuRef, isMenuOpen, closeMenu);
    useDismissOnOutside(alertsRef, isAlertsOpen && !isAlertsLocked, closeAlerts);

    const clientPath = `/client/${client.id}/`;
    const hasControlLines = Number(client.business_units_count || 0) > 0;

    // Click en cualquier parte "muerta" de la fila abre el panel. Los controles reales (links,
    // botones, el menú) manejan lo suyo y no deben disparar la navegación de la fila.
    const handleRowClick = (event) => {
        if (event.target.closest('a, button, .cv2-menu, .cv2-linepick, .cv2-alertpop, .modal')) return;
        props.onOpenClient(clientPath);
    };

    const renderMemoryChip = () => {
        if (!memoryEnabled) return null;
        const status = client.memory_status;

        if (status === 'none') {
            return (
                <span className="cv2-tip">
                    <Link
                        className="cv2-mem cv2-mem--none"
                        to={`/client/${client.id}/memory`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Plus size={11} weight="bold"/>
                        {t('clients_v2.memory_add')}
                    </Link>
                    <span className="warning-tooltip -below">
                        <span className="warning-tooltip-body">
                            <span className="warning-tooltip-text">{t('clients_v2.memory_add_tip')}</span>
                        </span>
                    </span>
                </span>
            );
        }

        if (status === 'active' || status === 'dormant') {
            const isActive = status === 'active';
            return (
                <span className="cv2-tip">
                    <span className={`cv2-mem ${isActive ? 'cv2-mem--active' : 'cv2-mem--dormant'}`}>
                        {isActive && <Lightning size={11} weight="fill"/>}
                        {isActive ? t('clients_v2.memory_active') : t('clients_v2.memory_dormant')}
                    </span>
                    <span className="warning-tooltip -below">
                        <span className="warning-tooltip-body">
                            <span className="warning-tooltip-text">
                                {isActive ? t('clients_v2.memory_active_tip') : t('clients_v2.memory_dormant_tip')}
                            </span>
                        </span>
                    </span>
                </span>
            );
        }

        return null;
    };

    const renderTeam = () => {
        const team = client.team || [];
        if (!team.length) return null;
        const visible = team.slice(0, MAX_VISIBLE_TEAM);
        const hidden = team.slice(MAX_VISIBLE_TEAM);
        return (
            <span className="cv2-team">
                {visible.map((member) => {
                    const isMe = String(member.id) === String(currentUserId);
                    const label = isMe ? `${member.name} ${t('clients_v2.team_you')}` : member.name;
                    return (
                        <span className="cv2-tip" key={member.id}>
                            <span className={`cv2-team__av ${isMe ? '-me' : ''}`} aria-label={label}>
                                {nameInitials(member.name)}
                            </span>
                            <span className="warning-tooltip -below">
                                <span className="warning-tooltip-body">
                                    <span className="warning-tooltip-text">{label}</span>
                                </span>
                            </span>
                        </span>
                    );
                })}
                {hidden.length > 0 && (
                    <span className="cv2-tip">
                        <span className="cv2-team__av -rest">{t('clients_v2.team_more', {n: hidden.length})}</span>
                        <span className="warning-tooltip -below">
                            <span className="warning-tooltip-body">
                                <span className="warning-tooltip-text">
                                    {hidden.map((member) => member.name).join(' · ')}
                                </span>
                            </span>
                        </span>
                    </span>
                )}
            </span>
        );
    };

    const renderIdentity = () => {
        const lines = Number(client.business_units_count || 0);
        const linesLabel = lines === 1
            ? t('clients_v2.control_lines_one')
            : t('clients_v2.control_lines_many', {n: lines});

        return (
            <div className="cv2-cell cv2-identity">
                {/* El logo del cliente, o sus iniciales en su color (D8 de la tanda iVirtual): con
                    todas las filas en gris, ningún cliente se reconocía de un vistazo. */}
                <ClientAvatar id={client.id} name={client.name} logoUrl={clientIconUrl(client)} className="cv2-avatar"/>
                <span className="cv2-identity__txt">
                    <Link className="cv2-identity__name" to={clientPath}>{client.name}</Link>
                    <span className="cv2-identity__sub">
                        {/* Qué es una línea, con la definición única del producto (card mFHzKeNK):
                            el conteo es la primera vez que la agencia lee el término en el listado.
                            `-below-left` porque el rótulo arranca contra el borde izquierdo. Enfocable
                            y vinculado a su cartel (C-SEGFE-06 del review): con sólo hover, quien
                            navega con teclado no llegaba nunca a la definición. */}
                        <span className="cv2-tip cv2-lines-count" tabIndex={0}
                              aria-describedby={linesDefinitionId}>
                            <span className="cv2-tip__label">{linesLabel}</span>
                            <span className="warning-tooltip -below-left">
                                <span className="warning-tooltip-body">
                                    <span className="warning-tooltip-text" id={linesDefinitionId}>
                                        {lineDefinition(t)}
                                    </span>
                                </span>
                            </span>
                        </span>
                        {!client.active && <span className="cv2-tag">{t('clients_v2.inactive_tag')}</span>}
                        {!client.assigned_to_me && <span className="cv2-tag">{t('clients_v2.unassigned_tag')}</span>}
                        {renderMemoryChip()}
                    </span>
                </span>
                {renderTeam()}
            </div>
        );
    };

    // Lo invertido, SIEMPRE en rol secundario (ver `renderSpend`). Devuelve null cuando el gasto no
    // se puede expresar: el backend manda `total_spend: null` si el cliente gasta en varias monedas
    // y la conversión no se resolvió, y resolverlo como 0 mostraría en cero a un cliente que
    // invirtió millones. En ese caso se muestra el desglose que YA viaja en el payload
    // ("ARS 1,2M + USD 3,4K"), que convierte un "no sé" en un dato que se puede usar.
    const investedLabel = () => {
        if (!client.spend_unavailable) return formatMoney(client.currency, Number(client.total_spend || 0));
        const byCurrency = Object.entries(client.spend_by_currency || {})
            .sort(([, first], [, second]) => second - first)
            .map(([currency, amount]) => formatMoney(currency, amount));
        return byCurrency.length ? byCurrency.join(' + ') : null;
    };

    /**
     * Celda de presupuesto e inversión.
     *
     * El PRESUPUESTO va primero y en negrita: es el valor que la agencia configuró y contra el que
     * se lee todo lo demás; la inversión es un derivado que lo acompaña. Es el mismo criterio que
     * el Panel del cliente —lo configurado pesa más que lo consumido—, y esta fila es su puerta de
     * entrada: si las dos pantallas jerarquizan distinto, contestan distinto.
     */
    const renderSpend = () => {
        const budget = Number(client.total_budget || 0);
        const spend = Number(client.total_spend || 0);
        const invested = investedLabel();

        // Sin presupuesto configurado no hay dato de referencia que poner adelante: lo que la fila
        // tiene para decir es justamente que falta cargarlo, y la inversión pasa a ser el contexto.
        if (budget <= 0) {
            return (
                <div className="cv2-cell cv2-col-spend">
                    <div className="cv2-metric">
                        <span className="cv2-metric__main -empty">{t('clients_v2.no_budget')}</span>
                    </div>
                    <div className="cv2-muted">
                        {invested
                            ? t('clients_v2.spend_invested_month', {
                                spend: invested,
                                interpolation: {escapeValue: false},
                            })
                            : t('clients_v2.spend_mixed_currency')}
                    </div>
                </div>
            );
        }

        // Hay presupuesto pero no un gasto único que comparar: se muestra el presupuesto y el
        // desglose, sin barra. Un porcentaje acá sería inventado.
        if (client.spend_unavailable) {
            return (
                <div className="cv2-cell cv2-col-spend">
                    <div className="cv2-metric">
                        <span className="cv2-metric__main">{formatMoney(client.currency, budget)}</span>
                    </div>
                    <div className="cv2-muted">
                        {invested
                            ? t('clients_v2.spend_invested', {
                                spend: invested,
                                interpolation: {escapeValue: false},
                            })
                            : t('clients_v2.spend_mixed_currency')}
                    </div>
                </div>
            );
        }

        const pct = Math.min(spend / budget, 1) * 100;
        // La barra sigue al MISMO veredicto que el borde de la fila, con los cuatro estados. Un
        // booleano acá pintaba de rojo todo lo que no fuera verde, incluido el gris de "todavía no
        // hay evaluación": la fila terminaba contradiciéndose (borde gris, barra roja).
        const spendSeverity = spendState(client);
        // En «Requieren atención» por el presupuesto, el motivo va escrito debajo de la barra: sólo
        // en el tooltip, convivía con el «✓ Sin alertas» de la columna Estado (card 21t02Hjz). Dice
        // un porcentaje del presupuesto o de lo esperado, nunca puntos (D5 de la tanda iVirtual).
        const reason = attentionReason(client, severity, elapsedFraction);

        return (
            <div className="cv2-cell cv2-col-spend">
                <span className="cv2-tip cv2-spend">
                    <div className="cv2-metric">
                        <span className="cv2-metric__main">{formatMoney(client.currency, budget)}</span>
                        <span className="cv2-metric__secondary">
                            {t('clients_v2.spend_invested', {
                                spend: formatAmount(spend),
                                interpolation: {escapeValue: false},
                            })}
                        </span>
                    </div>
                    <div className="cv2-bar">
                        <span className={`cv2-bar__fill -${spendSeverity}`} style={{width: `${pct.toFixed(1)}%`}}/>
                        <span
                            className="cv2-bar__pace"
                            style={{left: `${(elapsedFraction * 100).toFixed(1)}%`}}
                            aria-label={t('clients_v2.pace_aria')}
                        />
                    </div>
                    {renderSpendTooltip(spend, budget)}
                </span>
                {reason && (
                    <div className="cv2-reason">
                        <span className="cv2-reason__txt" data-testid="cv2-reason">
                            {t(`clients_v2.reason.${reason.key}`,
                                reason.pct === undefined ? undefined : {pct: formatPct(reason.pct)})}
                        </span>
                        {/* Un gasto de 3 veces el presupuesto o más casi siempre es un presupuesto
                            viejo, no un desborde (D6): la fila lo pregunta y lleva a editar la
                            línea. Sólo llega con UNA línea (`attentionReason`, decisión de fede del
                            09/10), y va por `ControlLineCta` por su `fallbackPath`: sin
                            `business_units` (backend viejo) cae a la vista de líneas. */}
                        {reason.checkBudget && (
                            <ControlLineCta
                                lines={client.business_units}
                                label={t('clients_v2.reason.check_budget')}
                                buildPath={(line) => `/business-unit/${line.id}/`}
                                fallbackPath={`/client/${client.id}/lines`}
                                className="cv2-reason__cta"
                                icon={PencilSimple}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Tooltip de la barra: responde "¿llego bien a fin de mes?" — consumido, cuánto de lo esperado
    // a hoy, proyección al cierre a este ritmo y restante.
    //
    // La PROYECCIÓN la manda el backend (`client.projection`) y no se calcula acá: extrapolar
    // linealmente el mes (gasto ÷ fracción transcurrida) daba un número distinto del que muestra el
    // Panel del cliente para el mismo cliente, y la fila es su puerta de entrada. El backend la
    // calcula con el ritmo de los últimos días cerrados, sobre el mismo alcance deduplicado que el
    // gasto de al lado. Si el campo no viene —el BE y el FE no deployan a la vez— la línea no se
    // muestra: mejor una fila de menos que dos definiciones conviviendo.
    const renderSpendTooltip = (spend, budget) => {
        const consumedPct = Math.round((spend / budget) * 100);
        const expectedPct = Math.round(elapsedFraction * 100);
        const reason = spendReason(client, elapsedFraction);
        const state = spendState(client);
        const projection = client.projection || {};
        const projectionPct = projection.vs_budget_pct !== null && projection.vs_budget_pct !== undefined
            ? Math.round(projection.vs_budget_pct)
            : null;
        const remaining = budget - spend;
        // Los días que quedan salen del MISMO payload que la proyección, que los contó con el "hoy"
        // de la zona horaria del cliente. Calcularlos con el reloj del navegador los hacía diferir
        // en el borde del mes: el tooltip podía decir "quedan 0 días" arriba de una proyección que
        // sí sumaba un día más. El cálculo local queda de fallback para el rato en que el backend
        // viejo todavía no manda el campo.
        const now = new Date();
        const daysLeft = projection.remaining_days !== null && projection.remaining_days !== undefined
            ? projection.remaining_days
            : new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate() - now.getDate();

        // El MISMO número que el motivo de la fila (`spendReason`): consumido sobre esperado, en
        // porcentaje. Hasta el 09/10 era la resta de los dos («+448%» con 477 consumido y 29
        // esperado, cuando había gastado 16 veces lo esperado).
        const vsExpected = reason ? reason.vsExpectedPct : null;
        let deltaLabel = null;
        if (reason && reason.key === SPEND_REASON_NO_SPEND) {
            deltaLabel = t('clients_v2.spend_tip_no_spend');
        } else if (vsExpected === 100) {
            deltaLabel = t('clients_v2.spend_tip_delta_on');
        } else if (vsExpected !== null && vsExpected !== undefined) {
            deltaLabel = t('clients_v2.spend_tip_vs_expected', {pct: formatPct(vsExpected)});
        }

        return (
            <span className="warning-tooltip -below -rich">
                <span className="warning-tooltip-body">
                    <span className="cv2-spend-tip">
                        <span className="cv2-spend-tip__head">
                            {t('clients_v2.spend_tip_consumed', {pct: consumedPct})}
                        </span>
                        <span className="cv2-spend-tip__row">
                            <span className="cv2-spend-tip__label">{t('clients_v2.spend_tip_expected', {pct: expectedPct})}</span>
                            {deltaLabel && <span className={`cv2-spend-tip__delta -${state}`}>{deltaLabel}</span>}
                        </span>
                        {projection.amount !== null && projection.amount !== undefined && projectionPct !== null && (
                            <span className="cv2-spend-tip__row">
                                <span className="cv2-spend-tip__label">{t('clients_v2.spend_tip_projection')}</span>
                                <span className="cv2-spend-tip__value">
                                    {formatMoney(client.currency, projection.amount)} · {t('clients_v2.spend_tip_projection_pct', {pct: projectionPct})}
                                </span>
                            </span>
                        )}
                        <span className="cv2-spend-tip__row">
                            <span className="cv2-spend-tip__label">{t('clients_v2.spend_tip_remaining')}</span>
                            <span className="cv2-spend-tip__value">
                                {remaining >= 0
                                    ? formatMoney(client.currency, remaining)
                                    : t('clients_v2.spend_tip_over_budget', {amount: formatMoney(client.currency, Math.abs(remaining))})}
                                {daysLeft > 0 && ` · ${daysLeft === 1
                                    ? t('clients_v2.spend_tip_days_one')
                                    : t('clients_v2.spend_tip_days_many', {n: daysLeft})}`}
                            </span>
                        </span>
                        {/* El gasto es del mes calendario; el presupuesto, del período de cada
                            línea. Cuando alguna de las que suman va por otro período (una línea
                            eventual, típicamente), el porcentaje enfrenta dos ventanas distintas.
                            Decirlo es la diferencia entre un número raro y un número que miente. */}
                        {client.budget_covers_other_period && (
                            <span className="cv2-spend-tip__note">
                                {t('clients_v2.spend_tip_other_period')}
                            </span>
                        )}
                    </span>
                </span>
            </span>
        );
    };

    const renderGoals = () => {
        const total = Number(client.goals_total || 0);

        // Los objetivos NO viven en el cliente: viven en cada línea de control. Con UNA línea el
        // CTA abre directo el modal de objetivo de esa línea (`?objetivo=`, card 6zUFUONB — antes
        // iba a la edición completa de la línea); con varias (o sin conocerlas, payload viejo)
        // aterriza en la vista de líneas YA FILTRADA en las que no tienen objetivo (card gtAL5axI)
        // — verlas filtradas resuelve el "¿en cuál?" mejor que un menú a ciegas.
        if (!total) {
            const noGoalLinesPath = `/client/${client.id}/lines?filtro=sin-objetivo`;
            return (
                <div className="cv2-cell cv2-col-goals">
                    <ControlLineCta
                        lines={client.business_units}
                        label={t('clients_v2.goals_empty')}
                        buildPath={(line) => defineGoalPath(client.id, line.id)}
                        multiPath={noGoalLinesPath}
                        fallbackPath={noGoalLinesPath}
                    />
                </div>
            );
        }

        // Sin `goals_on_track` no hay ritmo que mostrar: solo cuántos objetivos tiene el cliente.
        if (!hasGoalsPace(client)) {
            return (
                <div className="cv2-cell cv2-col-goals">
                    <span className="cv2-goals__txt">
                        {total === 1
                            ? t('clients_v2.goals_total_only_one')
                            : t('clients_v2.goals_total_only_many', {n: total})}
                    </span>
                </div>
            );
        }

        const onTrack = Number(client.goals_on_track || 0);
        // Los que todavía no tienen evaluación (el objetivo recién guardado, card RVO9leLh) no van
        // ni en ritmo ni atrasados: el color y el conteo de riesgo salen de los evaluados.
        const evaluated = evaluatedGoalsTotal(client);

        // Ninguno evaluado todavía: se cuenta cuántos hay, en neutro, en vez de un «0 de 1 en
        // ritmo» con la barrita roja. El tooltip los lista como «sin datos aún».
        if (!evaluated) {
            return (
                <div className="cv2-cell cv2-col-goals">
                    <span className="cv2-tip cv2-goals" data-testid="cv2-goals-pending">
                        <span className="cv2-goals__row">
                            <span className="cv2-dot -gray"/>
                            <span className="cv2-goals__txt">
                                {total === 1
                                    ? t('clients_v2.goals_total_only_one')
                                    : t('clients_v2.goals_total_only_many', {n: total})}
                            </span>
                        </span>
                        {renderGoalsTooltip()}
                    </span>
                </div>
            );
        }

        const state = goalsState(onTrack, evaluated);
        const pending = total - evaluated;
        // Una barrita por objetivo (verde en ritmo, roja atrasado, neutra sin evaluar), como la
        // propuesta original de Pablo. El detalle (qué objetivo es cada uno) vive en el tooltip.
        const pips = Array.from({length: total}, (_, i) => (
            <i key={i} className={i < onTrack ? '-ok' : (i < evaluated ? '-risk' : '-pending')}/>
        ));

        // El conteo es sobre los EVALUADOS, igual que el color: «1 de 2 en ritmo» con el punto verde
        // se leía como uno atrasado (review C-app-04). Los pendientes se nombran aparte.
        return (
            <div className="cv2-cell cv2-col-goals">
                <span className="cv2-tip cv2-goals">
                    <span className="cv2-goals__row">
                        <span className={`cv2-dot -${state}`}/>
                        <span className="cv2-goals__txt">
                            {t('clients_v2.goals_on_track', {onTrack, total: evaluated})}
                            <span className="cv2-goals__state">{t('clients_v2.goals_on_track_suffix')}</span>
                            {pending > 0 && (
                                <span className="cv2-goals__state" data-testid="cv2-goals-pending-count">
                                    {` · ${t('clients_v2.goals_pending', {count: pending})}`}
                                </span>
                            )}
                        </span>
                    </span>
                    <span className="cv2-goals__pips">{pips}</span>
                    {renderGoalsTooltip()}
                </span>
            </div>
        );
    };

    const GOAL_LEVEL_FALLBACK_KEY = {
        BU: 'clients_v2.goals_level_bu',
        ACCOUNT: 'clients_v2.goals_level_account',
        CAMPAIGN: 'clients_v2.goals_level_campaign',
        ADGROUP: 'clients_v2.goals_level_adgroup',
    };
    const MAX_TOOLTIP_GOALS = 8;

    // Lista de objetivos del cliente al pasar el mouse: atrasados primero, después los que todavía
    // no tienen datos, al final los que van en ritmo.
    const renderGoalsTooltip = () => {
        const detail = client.goals_detail || [];
        if (!detail.length) return null;
        const rank = (goal) => (goal.on_track === false ? 0 : goal.on_track === null ? 1 : 2);
        const sorted = detail.slice().sort((a, b) => rank(a) - rank(b));
        const visibleGoals = sorted.slice(0, MAX_TOOLTIP_GOALS);
        const rest = sorted.length - visibleGoals.length;

        return (
            <span className="warning-tooltip -below -rich">
                <span className="warning-tooltip-body">
                    <span className="cv2-goals-tip">
                        {visibleGoals.map((goal, index) => {
                            const tone = goal.on_track === false ? '-risk' : goal.on_track === null ? '-nodata' : '-ok';
                            const label = goal.item_label || t(GOAL_LEVEL_FALLBACK_KEY[goal.level] || 'clients_v2.goals_level_bu');
                            const hasValues = goal.value !== null && goal.value !== undefined;
                            return (
                                <span className="cv2-goals-tip__row" key={index}>
                                    <i className={`cv2-goals-tip__dot ${tone}`}/>
                                    <span className="cv2-goals-tip__metric">{getMetricName(t, goal.metric)}</span>
                                    <span className="cv2-goals-tip__label">{label}</span>
                                    {hasValues && (
                                        <span className="cv2-goals-tip__values">
                                            {goal.target !== null && goal.target !== undefined
                                                ? t('clients_v2.goals_value', {
                                                    value: formatGoalValue(goal.value),
                                                    target: formatGoalValue(goal.target),
                                                })
                                                : formatGoalValue(goal.value)}
                                        </span>
                                    )}
                                    {goal.on_track === null && (
                                        <span className="cv2-goals-tip__nodata">{t('clients_v2.goals_no_data')}</span>
                                    )}
                                </span>
                            );
                        })}
                        {rest > 0 && <span className="cv2-goals-tip__more">{t('clients_v2.goals_more', {n: rest})}</span>}
                    </span>
                </span>
            </span>
        );
    };

    // "hace 5 min" / "hace 17 h" / "hace 12 d" / "hace 8 meses" / "hace 2 años". Escala de unidad a
    // propósito: una cuenta caída hace 913 días es real (las hay), pero ese número no se lee — a
    // esa escala lo que comunica es "hace años". Siempre redondea hacia abajo.
    // Devuelve null si no hay fecha (nunca se chequeó).
    const formatElapsed = (isoDate) => {
        if (!isoDate) return null;
        const elapsedMinutes = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
        if (!Number.isFinite(elapsedMinutes) || elapsedMinutes < 0) return null;
        if (elapsedMinutes < 60) return t('clients_v2.elapsed_minutes', {n: Math.max(1, elapsedMinutes)});

        const elapsedHours = Math.floor(elapsedMinutes / 60);
        if (elapsedHours < 24) return t('clients_v2.elapsed_hours', {n: elapsedHours});

        const elapsedDays = Math.floor(elapsedHours / 24);
        if (elapsedDays < 60) return t('clients_v2.elapsed_days', {n: elapsedDays});

        const elapsedMonths = Math.floor(elapsedDays / 30);
        if (elapsedMonths < 24) {
            return elapsedMonths === 1
                ? t('clients_v2.elapsed_month_one')
                : t('clients_v2.elapsed_months', {n: elapsedMonths});
        }

        const elapsedYears = Math.floor(elapsedMonths / 12);
        return elapsedYears === 1
            ? t('clients_v2.elapsed_year_one')
            : t('clients_v2.elapsed_years', {n: elapsedYears});
    };

    // Ficha de cada cuenta: cuál es (nombre + id de la plataforma), en qué moneda reporta y cómo
    // está su conexión. Cuando NO conecta muestra lo mismo que la pantalla de Conexiones —el motivo
    // y desde cuándo—, porque "sin conexión" a secas no dice ni qué pasó ni si es urgente.
    const renderAccountsTooltip = (accounts) => (
        <span className="warning-tooltip -below -rich">
            <span className="warning-tooltip-body">
                <span className="cv2-acct-list">
                    {accounts.map((account, index) => {
                        const mismatch = accountCurrencyMismatch(account, client.currency);
                        const disconnected = account.connected === false;
                        const downSince = disconnected ? formatElapsed(account.disconnected_since) : null;
                        const lastCheck = formatElapsed(account.last_check);
                        return (
                            <span className="cv2-acct" key={index}>
                                {/* Logo del canal + nombre + id en la MISMA línea: es el patrón del
                                    resto del front (la cuenta se identifica por su plataforma, y el
                                    id acompaña al nombre en vez de colgar debajo). El logo también
                                    resuelve el caso de dos cuentas de plataformas distintas con
                                    nombres parecidos. */}
                                <span className="cv2-acct__head">
                                    <ChannelLogo
                                        channel={account.channel}
                                        height={14} width={14}
                                        hspace={0} vspace={0} marginLeft={0} padding={0}
                                    />
                                    <span className="cv2-acct__name">{account.name}</span>
                                    {account.account_id && (
                                        <span className="cv2-acct__id">{account.account_id}</span>
                                    )}
                                    {account.currency && (
                                        <span className={`cv2-acct__currency ${mismatch ? '-warn' : ''}`}>
                                            {account.currency}
                                        </span>
                                    )}
                                </span>

                                {disconnected ? (
                                    <span className="cv2-acct__state -down">
                                        {downSince
                                            ? t('clients_v2.accounts_down_since', {elapsed: downSince})
                                            : t('clients_v2.accounts_disconnected')}
                                    </span>
                                ) : (
                                    <span className="cv2-acct__state -ok">{t('clients_v2.accounts_connected')}</span>
                                )}

                                {/* `resolveErrorKind` y no `account.error_kind` crudo: un kind nuevo
                                    del backend imprimía la key de i18n sin traducir en la fila
                                    ("clients_v2.error_kind.SHEET_DATA_INVALID"). Ahora degrada al
                                    genérico, igual que en el tooltip del Tablero. */}
                                {disconnected && account.error_kind && (
                                    <span className="cv2-acct__reason">
                                        {t(`clients_v2.error_kind.${resolveErrorKind(account.error_kind)}`)}
                                    </span>
                                )}

                                {mismatch && (
                                    <span className="cv2-acct__reason -warn">
                                        {t('clients_v2.accounts_currency_warn')}
                                    </span>
                                )}

                                <span className="cv2-acct__checked">
                                    {lastCheck
                                        ? t('clients_v2.accounts_checked', {elapsed: lastCheck})
                                        : t('clients_v2.accounts_never_checked')}
                                </span>
                            </span>
                        );
                    })}
                </span>
            </span>
        </span>
    );

    const renderAccounts = () => {
        const channels = client.channels || [];
        const accounts = client.accounts || [];

        if (!channels.length) {
            return (
                <div className="cv2-cell cv2-col-accounts">
                    <Link
                        className="cv2-ghost-cta"
                        to={`/client/${client.id}/connections`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Plus size={12} weight="bold"/>
                        {t('clients_v2.accounts_empty')}
                    </Link>
                </div>
            );
        }

        const visible = channels.slice(0, MAX_VISIBLE_ACCOUNTS);
        const hiddenChannels = channels.slice(MAX_VISIBLE_ACCOUNTS);
        const rest = hiddenChannels.length;
        const hiddenAccounts = accounts.filter((account) => hiddenChannels.includes(account.channel));
        const hiddenSeverity = accountsSeverity(hiddenAccounts, client.currency);
        const downCount = downAccountsCount(client);

        // El punto sobre el logo (contrato §3 de la tanda iVirtual): rojo si alguna cuenta del canal
        // está caída, ámbar si sólo hay una con otra moneda que la del cliente. Una cuenta caída
        // tiene que verse SIN hover: si el aviso vive sólo dentro del tooltip, la fila se lee igual
        // que una sana y nadie se entera.
        // ⚠️ El modificador lleva prefijo (`-acct-down`, no `-down`): `_tables.scss` declara un
        // `.-down:after` GLOBAL con la flecha de Font Awesome, y el punto rojo salió a producción con
        // una «↓» negra encima del logo de al lado (QA-VIS-ivfe-01).
        const renderDot = (accountsSeverityValue) => accountsSeverityValue && (
            <span
                className={`cv2-plat__alert -acct-${accountsSeverityValue}`}
                role="img"
                aria-label={accountsSeverityValue === ACCOUNTS_DOWN
                    ? t('clients_v2.accounts_disconnected')
                    : t('clients_v2.accounts_currency_warn')}
            />
        );

        return (
            <div className="cv2-cell cv2-col-accounts">
                <span className="cv2-plat-stack">
                    {visible.map((channel, index) => {
                        const channelAccounts = accounts.filter((account) => account.channel === channel);
                        const channelSeverity = accountsSeverity(channelAccounts, client.currency);
                        // Los logos se solapan y cada uno se pinta encima del anterior, así que el punto
                        // (arriba a la derecha) quedaba debajo del logo siguiente cuando los dos tenían
                        // alerta (QA-VIS-ivfe-02). Un logo con alerta queda por encima de los que tiene a
                        // la derecha: el primero en 3, el segundo en 2, el tercero en 1 (`.-has-alert`).
                        const alertLayer = channelSeverity ? {'--cv2-alert-z': MAX_VISIBLE_ACCOUNTS - index} : undefined;
                        return (
                            <span className={`cv2-tip ${channelSeverity ? '-has-alert' : ''}`} key={`${channel}-${index}`}
                                  style={alertLayer}>
                                <span className="cv2-plat">
                                    <ChannelLogo channel={channel} height={18} width={18} hspace={0} vspace={0} marginLeft={0}/>
                                </span>
                                {renderDot(channelSeverity)}
                                {channelAccounts.length > 0 && renderAccountsTooltip(channelAccounts)}
                            </span>
                        );
                    })}
                </span>
                {rest > 0 && (
                    <span className={`cv2-tip ${hiddenSeverity ? '-has-alert' : ''}`}>
                        <span className="cv2-more">{t('clients_v2.accounts_more', {n: rest})}</span>
                        {renderDot(hiddenSeverity)}
                        {hiddenAccounts.length > 0 && renderAccountsTooltip(hiddenAccounts)}
                    </span>
                )}
                {/* «1 cuenta caída» dicho en la fila, sin hover: el punto solo se perdía (la de RN Pad
                    llevaba 17 días caída). Cuenta las de TODO el cliente, también las del «+N». */}
                {downCount > 0 && (
                    <span className="cv2-acct-down" data-testid="cv2-accounts-down">
                        {t('clients_v2.accounts_down_count', {count: downCount})}
                    </span>
                )}
            </div>
        );
    };

    // Las categorías crudas de la bitácora agrupadas en los mismos "tipos" que usan los chips de
    // capas de la Bitácora. Los rótulos dicen QUÉ PASÓ, no el nombre interno de la capa: «Memoria y
    // reuniones» o «Configuración y cuentas» no se entendían ni conociendo Dashbo (card 109Ga0e0).
    const ACTIVITY_GROUPS = [
        {cats: ['EXTERNAL', 'MEMORY_OPS'], labelKey: 'clients_v2.act_external'},
        {cats: ['ALERTS'], labelKey: 'clients_v2.act_alerts'},
        {cats: ['BUDGET'], labelKey: 'clients_v2.act_budget'},
        {cats: ['CONFIGURATION', 'STATUS', 'ACCOUNTS', 'INTEGRATIONS', 'REPORTS', 'AGENT'], labelKey: 'clients_v2.act_config'},
        {cats: ['COMMENT'], labelKey: 'clients_v2.act_comments'},
        {cats: ['TEAM'], labelKey: 'clients_v2.act_team'},
    ];

    // Deep-link a la Bitácora con la MISMA búsqueda que cuenta el sparkline (14 días, todas las
    // capas menos plataforma). Solo si la agencia tiene la Bitácora (memoria habilitada).
    const activityBitacoraPath = memoryEnabled
        ? `/client/${client.id}/activity-log?layers=${ACTIVITY_DEEP_LINK_LAYERS}&date_from=${localDateDaysAgo(13)}`
        : null;

    const renderActivityTooltip = (total) => {
        const breakdown = client.activity_breakdown_14d || {};
        const grouped = ACTIVITY_GROUPS
            .map((group) => ({
                labelKey: group.labelKey,
                count: group.cats.reduce((acc, cat) => acc + (Number(breakdown[cat]) || 0), 0),
            }))
            .filter((group) => group.count > 0)
            .sort((a, b) => b.count - a.count);
        const groupedTotal = grouped.reduce((acc, group) => acc + group.count, 0);
        const other = total - groupedTotal;

        return (
            <span className="warning-tooltip -below -rich">
                <span className="warning-tooltip-body">
                    <span className="cv2-act-tip">
                        <span className="cv2-act-tip__total">{t('clients_v2.act_total', {count: total})}</span>
                        {grouped.map((group) => (
                            <span className="cv2-act-tip__row" key={group.labelKey}>
                                <span className="cv2-act-tip__label">{t(group.labelKey)}</span>
                                <span className="cv2-act-tip__count">{group.count}</span>
                            </span>
                        ))}
                        {other > 0 && (
                            <span className="cv2-act-tip__row">
                                <span className="cv2-act-tip__label">{t('clients_v2.act_other')}</span>
                                <span className="cv2-act-tip__count">{other}</span>
                            </span>
                        )}
                        {activityBitacoraPath && (
                            <Link
                                className="cv2-act-tip__open"
                                to={activityBitacoraPath}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {t('clients_v2.activity_open_bitacora')}
                                <ArrowRight size={12} weight="bold"/>
                            </Link>
                        )}
                    </span>
                </span>
            </span>
        );
    };

    const renderActivity = () => {
        const series = client.activity_14d || [];
        const total = activityTotal(series);

        if (!total) {
            return (
                <div className="cv2-cell cv2-col-activity">
                    <span className="cv2-muted">{t('clients_v2.activity_empty')}</span>
                </div>
            );
        }

        const trend = activityTrend(series);
        const TrendIcon = TREND_ICON[trend];
        const content = (
            <>
                <Sparkline values={series}/>
                <span className="cv2-act-meta">
                    <span className="cv2-act-meta__total">{total}</span>
                    <span className="cv2-act-meta__label">{t('clients_v2.activity_total')}</span>
                    <TrendIcon size={11} weight="bold" className={`cv2-act-meta__trend -${trend}`}/>
                </span>
                {renderActivityTooltip(total)}
            </>
        );

        return (
            <div className="cv2-cell cv2-col-activity">
                {activityBitacoraPath ? (
                    <Link className="cv2-tip cv2-act" to={activityBitacoraPath} onClick={(e) => e.stopPropagation()}>
                        {content}
                    </Link>
                ) : (
                    <span className="cv2-tip cv2-act">{content}</span>
                )}
            </div>
        );
    };

    // Los contadores de alertas salen del payload del listado, salvo que el usuario haya silenciado
    // algo desde el popover: ahí manda lo que devolvió esa acción, para que el número baje en el
    // acto y no en la próxima carga de la pantalla.
    const alertsData = alertCounts || client;

    // Las alertas que el usuario silenció NO entran en el número —si entraran, silenciar no lo
    // bajaría y la acción se sentiría rota—, pero tampoco desaparecen: "no tenés alertas" y "tenés
    // 3 que apagaste" son dos situaciones distintas. Van en una línea aparte, con su vencimiento.
    // `alerts_snoozed_until` en `null` con silenciadas > 0 significa que alguna NO VENCE NUNCA, así
    // que ahí no se promete ninguna fecha.
    const renderSnoozedNote = () => {
        const snoozed = Number(alertsData.alerts_snoozed_count || 0);
        if (!snoozed) return null;

        const until = snoozedUntilLabel(alertsData.alerts_snoozed_until, i18n.language);
        if (!until) {
            return snoozed === 1
                ? t('clients_v2.alerts_snoozed_one_forever')
                : t('clients_v2.alerts_snoozed_many_forever', {n: snoozed});
        }
        return snoozed === 1
            ? t('clients_v2.alerts_snoozed_one', {date: until})
            : t('clients_v2.alerts_snoozed_many', {n: snoozed, date: until});
    };

    // El detalle, anclado al elemento que lo abre. El ref envuelve a los dos: si sólo envolviera al
    // panel, el click que lo abre caería "afuera" y lo cerraría en el mismo evento.
    const renderAlertsTrigger = (trigger) => (
        <span className="cv2-alerts" key="cv2-alerts" ref={alertsRef}>
            {trigger}
            <AlertsPopover
                clientId={client.id}
                clientName={client.name}
                isOpen={isAlertsOpen}
                memoryEnabled={memoryEnabled}
                onCountsChange={setAlertCounts}
                onInteractionLock={setIsAlertsLocked}
            />
        </span>
    );

    // Sin stopPropagation: `handleRowClick` ya ignora los <button>, y frenar el evento nativo en
    // el root de React dejaba sordo al listener de document de `useDismissOnOutside` — abrir el
    // popover de una fila no cerraba el de otra (ni un menu "..." abierto).
    const toggleAlerts = () => setIsAlertsOpen((wasOpen) => !wasOpen);

    const renderStatus = () => {
        if (!hasControlLines) {
            return <div className="cv2-cell cv2-col-status"><span className="cv2-dash">—</span></div>;
        }

        const snoozedNote = renderSnoozedNote();
        const alerts = Number(alertsData.alerts_count || 0);
        const pendingPlans = mediaPlanEnabled ? Number(client.media_plan_pending || 0) : 0;
        // Hechos esperando curado. Es trabajo pendiente del equipo, no una alerta del cliente: hasta
        // que alguien los apruebe no entran al contexto del agente, y el único lugar donde se veían
        // era adentro del tab Memoria del cliente.
        const pendingFacts = memoryEnabled ? Number(client.memory_pending_facts || 0) : 0;

        // "Sin alertas" NO es clickeable (no hay nada que mostrar), pero la línea de silenciadas sí:
        // si no, el usuario que silenció la última alerta se queda sin ninguna forma de reactivarla
        // desde acá — el contador cae a cero y el detalle se vuelve inalcanzable.
        if (!alerts && !pendingPlans && !pendingFacts) {
            // Con el presupuesto en rojo o ámbar, «Sin alertas» contradecía a la barra de la misma
            // fila (C-11, 15/10): la alerta de ritmo no se registra en la primera evaluación ni
            // mientras sigue en crítico, así que una línea puede nacer roja y quedarse sin alerta. No
            // es un botón: el clic en la fila abre el Panel, como siempre.
            const offPace = [SEVERITY_RED, SEVERITY_GOLD].includes(spendState(client));
            return (
                <div className="cv2-cell cv2-col-status">
                    {offPace ? (
                        <span className="cv2-pill cv2-pill--pace">
                            <Warning size={14} weight="bold"/>
                            {t('clients_v2.off_pace')}
                        </span>
                    ) : (
                        <span className="cv2-pill cv2-pill--ok">
                            <CheckCircle size={14} weight="bold"/>
                            {t('clients_v2.no_alerts')}
                        </span>
                    )}
                    {snoozedNote && renderAlertsTrigger(
                        <button type="button" className="cv2-muted cv2-linkish"
                                aria-expanded={isAlertsOpen} onClick={toggleAlerts}>
                            {snoozedNote}
                        </button>,
                    )}
                </div>
            );
        }

        // La pill cuenta PROBLEMAS vigentes (uno por cosa rota, no uno por vez que sonó) y abre el
        // detalle de esos problemas. El link a la Bitácora se mudó al pie del popover: ahí sigue
        // mostrando las ráfagas, o sea MÁS filas que el número de la pill. La divergencia es
        // deliberada — ver el docstring de `AgencyClientsOverviewService`.
        const alertsLabel = alerts === 1 ? t('clients_v2.alerts_one') : t('clients_v2.alerts_many', {n: alerts});

        return (
            <div className="cv2-cell cv2-col-status">
                {alerts > 0 && renderAlertsTrigger(
                    <button type="button" className="cv2-pill cv2-pill--alert"
                            aria-expanded={isAlertsOpen} onClick={toggleAlerts}>
                        <Warning size={14} weight="fill"/>
                        {alertsLabel}
                    </button>,
                )}
                {snoozedNote && <span className="cv2-muted">{snoozedNote}</span>}
                {pendingPlans > 0 && (
                    <span className="cv2-tip">
                        <Link
                            className="cv2-pill cv2-pill--plan"
                            to={`/client/${client.id}/media-plan`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <ArrowsClockwise size={13} weight="bold"/>
                            {t('clients_v2.media_plan_pill')}
                        </Link>
                        <span className="warning-tooltip -below">
                            <span className="warning-tooltip-body">
                                <span className="warning-tooltip-text">{t('clients_v2.media_plan_pill_tip')}</span>
                            </span>
                        </span>
                    </span>
                )}
                {pendingFacts > 0 && (
                    <span className="cv2-tip">
                        <Link
                            className="cv2-pill cv2-pill--facts"
                            to={`/client/${client.id}/memory/facts`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <BookOpen size={13} weight="bold"/>
                            {t('clients_v2.memory_facts_pill', {count: pendingFacts})}
                        </Link>
                        <span className="warning-tooltip -below">
                            <span className="warning-tooltip-body">
                                <span className="warning-tooltip-text">
                                    {t('clients_v2.memory_facts_pill_tip')}
                                </span>
                            </span>
                        </span>
                    </span>
                )}
            </div>
        );
    };

    const renderSetup = () => (
        <div className="cv2-setup">
            <Warning size={16} weight="fill"/>
            <span className="cv2-setup__txt">{t('clients_v2.setup_no_lines')}</span>
            <Link className="cv2-setup__cta" to={clientPath} onClick={(e) => e.stopPropagation()}>
                {t('clients_v2.setup_cta')}
                <ArrowRight size={13} weight="bold"/>
            </Link>
        </div>
    );

    const renderMenu = () => (
        <div className="cv2-cell cv2-actions">
            <div className="cv2-menu" ref={menuRef}>
                <button
                    type="button"
                    className="cv2-icon-btn"
                    aria-label={t('clients_v2.row_menu_aria')}
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen}
                    onClick={() => setIsMenuOpen(open => !open)}
                >
                    <DotsThreeVertical size={16} weight="bold"/>
                </button>
                {isMenuOpen && (
                    <div className="cv2-menu__list">
                        <Link className="cv2-menu__item" to={clientPath} onClick={() => setIsMenuOpen(false)}>
                            <SquaresFour size={15}/>
                            {t('clients_v2.menu_open_panel')}
                        </Link>
                        <Link className="cv2-menu__item" to={`/client/${client.id}/memory`} onClick={() => setIsMenuOpen(false)}>
                            <Brain size={15}/>
                            {t('clients_v2.menu_memory')}
                        </Link>
                        <Link className="cv2-menu__item" to="/agency-team" onClick={() => setIsMenuOpen(false)}>
                            <UsersThree size={15}/>
                            {t('clients_v2.menu_team')}
                        </Link>
                        <Link className="cv2-menu__item" to={`/client/${client.id}/configuration`} onClick={() => setIsMenuOpen(false)}>
                            <Gear size={15}/>
                            {t('clients_v2.menu_configuration')}
                        </Link>
                        <span className="cv2-menu__divider"/>
                        {client.active ? (
                            <button
                                type="button"
                                className="cv2-menu__item"
                                onClick={() => { setIsMenuOpen(false); props.onDeactivate(client); }}
                            >
                                <Power size={15}/>
                                {t('clients_v2.menu_deactivate')}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="cv2-menu__item"
                                    onClick={() => { setIsMenuOpen(false); props.onActivate(client); }}
                                >
                                    <Power size={15}/>
                                    {t('clients_v2.menu_activate')}
                                </button>
                                <button
                                    type="button"
                                    className="cv2-menu__item -danger"
                                    onClick={() => { setIsMenuOpen(false); props.onDelete(client); }}
                                >
                                    <Trash size={15}/>
                                    {t('clients_v2.menu_delete')}
                                </button>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // Vista «Economía» (módulo Economía del cliente): la identidad y el ⋮ de siempre, y en el medio
    // las celdas de economía. No depende de tener líneas: la economía es del negocio, no de la pauta.
    if (props.view === VIEW_ECONOMICS) {
        return (
            <article
                className={`cv2-row cv2-grid -sev-${severity} ${client.active ? '' : '-inactive'}`}
                onClick={handleRowClick}
            >
                {renderIdentity()}
                <ClientEconomicsCells client={client}/>
                {renderMenu()}
            </article>
        );
    }

    // El recorrido de Clientes con cartera explica las celdas de UNA fila: la que el listado marca
    // con `guideRow` (la primera con líneas). El ancla se pone al armar la fila, sobre la raíz que
    // devuelva cada celda, para no repetirla en cada una de sus ramas.
    const guideCell = (cell, anchor) => (props.guideRow && cell ? cloneElement(cell, anchor) : cell);

    return (
        <article
            className={`cv2-row cv2-grid -sev-${severity} ${client.active ? '' : '-inactive'}`}
            onClick={handleRowClick}
        >
            {renderIdentity()}
            {hasControlLines ? (
                <>
                    {guideCell(renderSpend(), anchorAttr('clients_row_spend'))}
                    {guideCell(renderGoals(), anchorAttr('clients_row_goals'))}
                    {guideCell(renderAccounts(), anchorAttr('clients_row_accounts'))}
                    {guideCell(renderActivity(), anchorAttr('clients_row_activity'))}
                </>
            ) : renderSetup()}
            {guideCell(renderStatus(), anchorAttr('clients_row_status'))}
            {renderMenu()}
        </article>
    );
}

export default ClientV2Row;
